import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { formatApiError } from "@/lib/api";
import AuthShell from "@/components/AuthShell";
import { toast } from "sonner";
import { Eye, EyeOff, Phone, Lock } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    try {
      const notice = window.sessionStorage.getItem("pm_login_notice");
      if (notice) { setErr(notice); window.sessionStorage.removeItem("pm_login_notice"); }
    } catch { /* noop */ }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await login(mobile.trim(), password, remember);
      toast.success(`Welcome, ${u.name}!`);
      if (u.role === "admin") nav("/admin");
      else if (!u.recovery_complete) nav("/register?step=recovery");
      else nav(sessionStorage.getItem("pm_after_login") || "/app");
      sessionStorage.removeItem("pm_after_login");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title={t("login")} subtitle={t("app_name")}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="font-semibold text-sm flex items-center gap-1"><Phone className="w-4 h-4" /> {t("mobile")}</label>
          <input className="input-thali mt-1" inputMode="numeric" pattern="[0-9]*" maxLength={10}
            value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))} placeholder="10-digit mobile" data-testid="login-mobile" />
        </div>
        <div>
          <label className="font-semibold text-sm flex items-center gap-1"><Lock className="w-4 h-4" /> {t("password")}</label>
          <div className="relative mt-1">
            <input className="input-thali pr-12" type={show ? "text" : "password"} value={password}
              onChange={(e) => setPassword(e.target.value)} placeholder="Your password" data-testid="login-password" />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-thali-ink/50" aria-label="Toggle password">
              {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="w-4 h-4 accent-thali-green" data-testid="login-remember" />
            {t("remember_me")}
          </label>
          <Link to="/forgot" className="text-thali-green font-semibold" data-testid="login-forgot">{t("forgot_password")}</Link>
        </div>
        {err && <div className="text-red-600 text-sm bg-red-50 rounded-lg p-2" data-testid="login-error">{err}</div>}
        <button disabled={busy} className="btn-3d-green w-full" data-testid="login-submit">{busy ? "…" : t("login")}</button>
      </form>
      <p className="text-center text-sm text-thali-ink/70 mt-4">
        New here? <Link to="/register" className="text-thali-green font-bold" data-testid="login-to-register">{t("register")}</Link>
      </p>
    </AuthShell>
  );
}
